import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { conformidadAPI } from '@/lib/api';
import { Loader2, Check } from 'lucide-react';

/**
 * La única pantalla del producto que se abre **sin cuenta**.
 *
 * El que pidió el caso puede no tener una: escribió un WhatsApp o un correo y nunca entró a la
 * consola. Exigirle una cuenta para contestar una pregunta de un renglón convertiría la medición
 * en una medición de quién tiene cuenta.
 *
 * **Es una sola pregunta y se contesta en un toque.** Cada campo agregado acá se paga en
 * respuestas que no llegan, y por eso el número envía solo, sin botón aparte.
 *
 * **El comentario va arriba del número y no abajo** (corregido el 2026-09-30). Estaba al revés,
 * con la advertencia «escribí el comentario antes de elegir el número» **debajo de todo**: la
 * instrucción llegaba después de la acción que pretendía guiar. La primera persona que contestó
 * tocó un número, la página se cerró, y su comentario no existió nunca — quedó grabado como
 * `comentario: ""`. Sigue siendo opcional y sigue enviándose en un toque; lo único que cambió es
 * que ahora el toque es lo último que se ve.
 *
 * **Un enlace que ya no sirve no es un error.** Vencido, ya contestado o inventado dicen lo mismo
 * y con el mismo tono: distinguirlos le diría a cualquiera que pruebe enlaces al azar cuándo pegó
 * con uno real, y a la persona que llegó de buena fe no le cambia nada.
 */
export default function ConformidadPage() {
  const { token } = useParams();
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [valor, setValor] = useState(0);
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState('');

  useEffect(() => {
    conformidadAPI.ver(token)
      .then(({ data }) => {
        setDatos(data);
        if (data.ya_respondida) setListo(data.agradecimiento);
      })
      .catch(() => setError('Este enlace ya no está disponible.'));
  }, [token]);

  const enviar = async (elegido) => {
    setEnviando(true);
    try {
      const { data } = await conformidadAPI.contestar(token, { valor: elegido, comentario });
      setListo(data.mensaje || 'Gracias.');
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo registrar la respuesta.');
    } finally {
      setEnviando(false);
    }
  };

  const Marco = ({ children }) => (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-sm p-8 space-y-5"
           data-testid="conformidad">
        {children}
      </div>
    </div>
  );

  if (error) {
    return (
      <Marco>
        <p className="text-sm text-zinc-600">{error}</p>
        <p className="text-xs text-zinc-400">
          Si querés decirnos algo igual, respondé el correo con el que te avisamos.
        </p>
      </Marco>
    );
  }

  if (!datos) {
    return (
      <Marco>
        <p className="text-xs text-zinc-500 flex items-center gap-2 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Un segundo…
        </p>
      </Marco>
    );
  }

  if (listo) {
    return (
      <Marco>
        <div className="flex items-center gap-2 text-green-700">
          <Check className="w-5 h-5" />
          <p className="text-sm font-medium" data-testid="conformidad-listo">{listo}</p>
        </div>
        <p className="text-xs text-zinc-400">Ya podés cerrar esta página.</p>
      </Marco>
    );
  }

  const opciones = Array.from({ length: datos.escala }, (_, i) => i + 1);

  return (
    <Marco>
      <div className="space-y-1">
        <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400">
          {datos.caso.ticket_id}
        </p>
        <p className="text-xs text-zinc-500">{datos.caso.titulo}</p>
      </div>

      <p className="text-base font-medium text-zinc-900">{datos.pregunta}</p>

      {/* **El comentario va arriba del número, y el aviso también.**
       *
       * Estaba al revés: los números primero, el campo de texto abajo, y debajo de todo la
       * advertencia «escribí el comentario antes de elegir el número». O sea que la instrucción
       * llegaba **después** de la acción que pretendía guiar — la primera persona que contestó
       * tocó un número, la página se cerró, y su comentario no existió nunca.
       *
       * Se conserva lo que el diseño buscaba: un solo toque envía. Lo que cambia es el orden,
       * para que ese toque sea lo último que se ve y no lo primero. */}
      <textarea
        data-testid="conformidad-comentario"
        value={comentario}
        onChange={(e) => setComentario(e.target.value)}
        placeholder="¿Querés contarnos algo más? (opcional)"
        className="w-full border border-zinc-300 rounded-sm p-2 text-xs h-20 focus:ring-1 focus:ring-brand-primary focus:outline-none resize-y"
      />

      <div className="pt-1">
        <p className="text-xs text-zinc-600 mb-1.5">
          Elegí un número para enviar. {comentario.trim() ? 'Se manda junto con tu comentario.' : ''}
        </p>
        <div className="flex gap-1.5 flex-wrap">
          {opciones.map((n) => (
            <button
              key={n}
              type="button"
              data-testid={`conformidad-valor-${n}`}
              disabled={enviando}
              onClick={() => { setValor(n); enviar(n); }}
              className={`w-10 h-10 rounded-sm border text-sm font-medium transition-colors
                ${valor === n
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'bg-white text-zinc-700 border-zinc-300 hover:border-brand-primary hover:text-brand-primary'}
                disabled:opacity-50`}
            >
              {n}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-zinc-400 mt-1">1 es lo peor, {datos.escala} es lo mejor.</p>
      </div>
    </Marco>
  );
}
